
(function () {
  'use strict';

  const SECTION = '[data-crp]';
  const CONTENT = '[data-crp-content]';
  const TRACK = '[data-crp-track]';
  const CARD = '[data-crp-card]';

  function initCarousel(section) {
    if (!section || section.dataset.crpInitialized === 'true') return;

    const content = section.querySelector(CONTENT);
    if (!content) return;

    section.dataset.crpInitialized = 'true';

    let track = null;
    let raf = null;
    let resizeObserver = null;
    let mutationObserver = null;

    function getPrevious() {
      return section.querySelector('[data-crp-prev]');
    }

    function getNext() {
      return section.querySelector('[data-crp-next]');
    }

    function getProgress() {
      return section.querySelector('[data-crp-progress]');
    }

    function getProgressBar() {
      return section.querySelector('[data-crp-progress-bar]');
    }

    function updateControls() {
      if (!track || !track.isConnected) return;

      if (raf !== null) {
        cancelAnimationFrame(raf);
      }

      raf = requestAnimationFrame(function () {
        raf = null;

        if (!track || !track.isConnected) return;

        const max = Math.max(
          0,
          track.scrollWidth - track.clientWidth
        );

        const current = Math.max(
          0,
          Math.min(track.scrollLeft, max)
        );

        const canScroll = max > 2;

        const previous = getPrevious();
        const next = getNext();
        const progress = getProgress();
        const progressBar = getProgressBar();

        if (previous) {
          previous.hidden = !canScroll;
          previous.disabled = !canScroll || current <= 2;
          previous.setAttribute(
            'aria-disabled',
            String(previous.disabled)
          );
        }

        if (next) {
          next.hidden = !canScroll;
          next.disabled = !canScroll || current >= max - 2;
          next.setAttribute(
            'aria-disabled',
            String(next.disabled)
          );
        }

        if (progress && progressBar) {
          progress.hidden = !canScroll;

          if (canScroll) {
            const visibleRatio = Math.min(
              1,
              track.clientWidth / track.scrollWidth
            );

            const scrollRatio = current / max;

            const thumbWidth = visibleRatio * 100;
            const travelPercent = (1 - visibleRatio) * 100;
            const translatePercent = scrollRatio * travelPercent;

            progressBar.style.width = thumbWidth + '%';
            progressBar.style.transform =
              'translateX(' + translatePercent + '%)';
          } else {
            progressBar.style.width = '100%';
            progressBar.style.transform = 'translateX(0)';
          }
        }
      });
    }

    function getStep() {
      if (!track) return 0;

      const card = track.querySelector(CARD);

      if (!card) {
        return track.clientWidth;
      }

      const styles = window.getComputedStyle(track);

      const gap =
        parseFloat(styles.columnGap) ||
        parseFloat(styles.gap) ||
        0;

      return card.getBoundingClientRect().width + gap;
    }

    function move(direction) {
      if (!track || !track.isConnected) return;

      const max = Math.max(
        0,
        track.scrollWidth - track.clientWidth
      );

      const step = getStep();

      if (!step || max <= 0) return;

      const target = Math.max(
        0,
        Math.min(max, track.scrollLeft + direction * step)
      );

      track.scrollTo({
        left: target,
        behavior: 'smooth'
      });

      updateControls();
    }

    function bindTrack() {
      const newTrack = content.querySelector(TRACK);

      if (!newTrack) return false;

      if (track === newTrack) {
        updateControls();
        return true;
      }

      if (track) {
        track.removeEventListener('scroll', updateControls);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      track = newTrack;

      track.addEventListener('scroll', updateControls, {
        passive: true
      });

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(function () {
          updateControls();
        });

        resizeObserver.observe(track);

        track.querySelectorAll(CARD).forEach(function (card) {
          resizeObserver.observe(card);
        });
      }

      updateControls();

      return true;
    }

    // Previous and Next button clicks
    section.addEventListener('click', function (event) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const button = target.closest(
        '[data-crp-prev], [data-crp-next]'
      );

      if (!button || !section.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      if (!track || !track.isConnected) {
        bindTrack();
      }

      if (!track) return;

      if (button.hasAttribute('data-crp-next')) {
        move(1);
      } else {
        move(-1);
      }
    });

    // Recalculate on window resize
    window.addEventListener(
      'resize',
      function () {
        bindTrack();
        updateControls();
      },
      { passive: true }
    );

    // If the product track already exists
    if (bindTrack()) {
      return;
    }

    // Load recommendations when the track is not present yet
    const url = section.dataset.crpUrl;

    if (url) {
      fetch(url, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error(
              'Related products request failed: ' + response.status
            );
          }

          return response.text();
        })
        .then(function (html) {
          const parsed = new DOMParser().parseFromString(
            html,
            'text/html'
          );

          const returnedSection = parsed.getElementById(
            section.id
          );

          const returnedContent = returnedSection
            ? returnedSection.querySelector(CONTENT)
            : null;

          if (!returnedContent) {
            content.innerHTML =
              '<p>No related products found.</p>';
            return;
          }

          content.innerHTML = returnedContent.innerHTML;

          bindTrack();
          updateControls();
        })
        .catch(function (error) {
          console.error(
            'Related products carousel error:',
            error
          );

          content.innerHTML =
            '<p>Unable to load related products.</p>';
        });
    }

    // Detect asynchronously inserted product cards
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(function () {
        if (bindTrack()) {
          updateControls();
        }
      });

      mutationObserver.observe(content, {
        childList: true,
        subtree: true
      });
    }
  }

  function initAll(scope) {
    const root = scope || document;

    if (root.matches && root.matches(SECTION)) {
      initCarousel(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(SECTION).forEach(function (section) {
        initCarousel(section);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      { once: true }
    );
  } else {
    initAll(document);
  }

  // Support Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
